import { useUrlSearchParams } from '@vueuse/core'

/**
 * Configuration for source detection
 */
const SEARCH_ENGINES = [
  'google.com',
  'google.co.uk',
  'google.ca',
  'google.com.au',
  'bing.com',
  'yahoo.com',
  'duckduckgo.com',
  'yandex.com',
]

const SOCIAL_PLATFORMS = [
  'facebook.com',
  'twitter.com',
  'x.com',
  'instagram.com',
  'linkedin.com',
  'tiktok.com',
  'youtube.com',
  'pinterest.com',
]

/**
 * Safe referrer detection for SSR compatibility
 */
const getReferrer = (): string | null => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }
  return document.referrer || null
}

/**
 * Get current domain for own-domain detection
 */
const getCurrentDomain = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }
  return window.location.hostname
}

/**
 * Check if domain belongs to our own properties
 */
const isOwnDomain = (domain: string): boolean => {
  const currentDomain = getCurrentDomain()

  const ownDomains = [
    currentDomain,
    'goodlifeplus.co.uk',
    'goodlifeplus.com',
    'localhost',
    '127.0.0.1',
  ]

  return ownDomains.some(
    (ownDomain) =>
      domain === ownDomain ||
      domain.endsWith('.' + ownDomain) ||
      ownDomain.includes(domain)
  )
}

/**
 * Check if domain is a search engine
 */
const isSearchEngine = (domain: string): boolean => {
  return SEARCH_ENGINES.some(
    (engine) =>
      domain === engine ||
      domain.endsWith('.' + engine) ||
      domain.includes(engine.split('.')[0])
  )
}

/**
 * Check if domain is a social media platform
 */
const isSocialMedia = (domain: string): boolean => {
  return SOCIAL_PLATFORMS.some(
    (platform) =>
      domain === platform ||
      domain.endsWith('.' + platform) ||
      domain.includes(platform.split('.')[0])
  )
}

/**
 * Extract parameter value handling URLSearchParams arrays
 */
const getParam = (
  utmParams: Record<string, string | string[]>,
  key: string
): string | null => {
  const value = utmParams[key]
  if (!value) {
    return null
  }
  return Array.isArray(value) ? value[0] : value
}

/**
 * Cached source to avoid repeated computation
 */
let cachedSource: string | null = null

/**
 * Lead Source Tracking Service
 *
 * Provides intelligent lead source detection and attribution functionality
 * with support for UTM parameters, referrer analysis, and SSR compatibility.
 *
 * Features:
 * - UTM parameter parsing with priority-based detection
 * - Referrer analysis for organic search and social media detection
 * - SSR/SSG compatibility with safe DOM access
 * - Type-safe parameter handling
 * - Performance optimization with optional caching
 * - Comprehensive error handling
 *
 * Source Detection Priority:
 * 1. UTM parameters (utm_source, utm_medium, utm_campaign) - highest priority
 * 2. Direct source/ref parameters - explicit attribution
 * 3. Referrer analysis (search engines, social media, external sites)
 * 4. Context-based defaults (provided source)
 * 5. Fallback to 'DIRECT' for unknown sources
 */
export class LeadSourceTracker {
  /**
   * Configuration constants for domain detection
   */
  public static readonly SEARCH_ENGINES = SEARCH_ENGINES
  public static readonly SOCIAL_PLATFORMS = SOCIAL_PLATFORMS

  /**
   * Determines the most accurate source for lead tracking with improved type safety and SSR compatibility
   *
   * @param utmParams - URL search parameters containing UTM and other tracking data
   * @param providedSource - Optional source override or context
   * @returns String representing the detected source (e.g., 'ORGANIC_SEARCH', 'SOCIAL', 'DIRECT')
   */
  public static determineSource(
    utmParams: Record<string, string | string[]>,
    providedSource?: string
  ): string {
    try {
      // Priority 1: UTM source (most accurate)
      const utmSource = getParam(utmParams, 'utm_source')
      if (utmSource) {
        return utmSource.toUpperCase()
      }

      // Priority 2: Direct source parameter
      const sourceParam = getParam(utmParams, 'source')
      if (sourceParam) {
        return sourceParam.toUpperCase()
      }

      // Priority 3: Campaign or referral parameters
      const utmCampaign = getParam(utmParams, 'utm_campaign')
      if (utmCampaign) {
        return 'CAMPAIGN'
      }

      const utmMedium = getParam(utmParams, 'utm_medium')
      if (utmMedium) {
        return utmMedium.toUpperCase()
      }

      const refParam = getParam(utmParams, 'ref')
      if (refParam) {
        return 'REFERRAL'
      }

      // Priority 4: Check referrer for organic sources (SSR-safe)
      const referrer = getReferrer()
      if (referrer) {
        try {
          const referrerDomain = new URL(referrer).hostname.toLowerCase()

          // Don't count our own domain as external referrer
          if (!isOwnDomain(referrerDomain)) {
            // Check for search engines
            if (isSearchEngine(referrerDomain)) {
              return 'ORGANIC_SEARCH'
            }

            // Check for social media
            if (isSocialMedia(referrerDomain)) {
              return 'SOCIAL'
            }

            // Other external referrers
            return 'REFERRER'
          }
        } catch (error) {
          console.warn('Error parsing referrer URL:', referrer, error)
        }
      }

      // Priority 5: Context-based defaults
      if (providedSource && providedSource !== 'ORGANIC') {
        return providedSource.toUpperCase()
      }

      // Priority 6: Default to DIRECT (most conservative assumption)
      return 'DIRECT'
    } catch (error) {
      console.warn('Error determining source:', error)
      return 'UNKNOWN'
    }
  }

  /**
   * Utility method to get the current source for lead tracking from URL parameters
   * Can be used by components to override or provide context
   * Includes caching to avoid repeated computation
   *
   * @param providedSource - Optional source override or context
   * @param forceRefresh - Force refresh of cached source
   * @returns String representing the current detected source
   */
  public static getCurrentSource(
    providedSource?: string,
    forceRefresh = false
  ): string {
    if (cachedSource && !forceRefresh && !providedSource) {
      return cachedSource
    }

    const utmParams = useUrlSearchParams()
    const mappedUtmParams = Object.fromEntries(
      Object.entries(utmParams).map(([key, value]) => {
        return [key.toLowerCase(), value]
      })
    )

    const source = this.determineSource(mappedUtmParams, providedSource)

    // Cache only if no providedSource to avoid caching contextual overrides
    if (!providedSource) {
      cachedSource = source
    }

    return source
  }

  /**
   * Clear the cached source (useful for testing or when URL parameters change significantly)
   */
  public static clearCache(): void {
    cachedSource = null
  }

  /**
   * Check if a domain is considered a search engine
   *
   * @param domain - Domain to check
   * @returns True if domain is a known search engine
   */
  public static isSearchEngine(domain: string): boolean {
    return isSearchEngine(domain)
  }

  /**
   * Check if a domain is considered a social media platform
   *
   * @param domain - Domain to check
   * @returns True if domain is a known social media platform
   */
  public static isSocialMedia(domain: string): boolean {
    return isSocialMedia(domain)
  }

  /**
   * Check if a domain belongs to our own properties
   *
   * @param domain - Domain to check
   * @returns True if domain is considered our own
   */
  public static isOwnDomain(domain: string): boolean {
    return isOwnDomain(domain)
  }

  /**
   * Get the current page referrer in a SSR-safe way
   *
   * @returns Referrer URL string or null if not available
   */
  public static getReferrer(): string | null {
    return getReferrer()
  }
}

export default LeadSourceTracker
